import { useLanguage } from '@/contexts/LanguageContext';
import { useState, useEffect } from 'react';
import { getAboutContent, type AboutContent, getTeamMembers, type TeamMember } from '@/services/api';
import { assetApi, processAssetMarkers, type Asset } from '@/services/assetApi';

interface AboutProps {
  imageUrl: string;
}

export function About({ imageUrl }: AboutProps) {
  const { t, language } = useLanguage();
  const [aboutContent, setAboutContent] = useState<AboutContent | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [expandedMemberId, setExpandedMemberId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      setLoading(true);
      try {
        const [content, members, assetsList] = await Promise.all([
          getAboutContent(),
          getTeamMembers(),
          assetApi.getAssets().catch(err => {
            console.error('Failed to load assets:', err);
            return []; // Return empty array if assets fail to load
          })
        ]);
        setAboutContent(content);
        setTeamMembers(members);
        setAssets(assetsList);
      } catch (error) {
        console.error('Failed to load about content:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  // Handle clicks on team members to toggle bio
  useEffect(() => {
    const handleTeamMemberClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const memberElement = target.closest('.team-member') as HTMLElement;
      
      if (memberElement) {
        const memberId = memberElement.getAttribute('data-member-id');
        if (!memberId) return;
        
        // Close all other bios
        const allBios = document.querySelectorAll('.team-member-bio-container');
        allBios.forEach(bio => {
          const bioElement = bio as HTMLElement;
          const bioId = bioElement.getAttribute('data-bio-id');
          if (bioId !== memberId) {
            bioElement.classList.remove('open');
          }
        });
        
        // Toggle the clicked member's bio
        const bioContainer = memberElement.querySelector(`[data-bio-id="${memberId}"]`) as HTMLElement;
        if (bioContainer) {
          bioContainer.classList.toggle('open');
        }
      }
    };
    
    // Add event listener to the document
    document.addEventListener('click', handleTeamMemberClick);
    
    return () => {
      document.removeEventListener('click', handleTeamMemberClick);
    };
  }, [teamMembers]);

  // Generate team members HTML for insertion
  const generateTeamHTML = () => {
    if (teamMembers.length === 0) return '';
    
    // Sort members by order
    const sortedMembers = [...teamMembers].sort((a, b) => (a.order || 0) - (b.order || 0));
    
    // Process bio text to create two-column timeline layout
    const processBioText = (bioHtml: string) => {
      // Split by line breaks (both <br> and </p><p>)
      const lines = bioHtml
        .replace(/<\/p>\s*<p>/gi, '<br>')
        .replace(/<p>/gi, '')
        .replace(/<\/p>/gi, '')
        .split(/<br\s*\/?>/gi)
        .filter(line => line.trim());
      
      const processedLines = lines.map(line => {
        const trimmedLine = line.trim();
        // Check if line contains pipe separator
        if (trimmedLine.includes('|')) {
          const [year, ...textParts] = trimmedLine.split('|');
          const text = textParts.join('|'); // In case text contains pipes
          // Replace ~ with <br> for line breaks in both columns
          const formattedYear = year.trim().replace(/~/g, '<br>');
          const formattedText = text.trim().replace(/~/g, '<br>');
          return `
            <div class="timeline-entry">
              <div class="timeline-year">${formattedYear}</div>
              <div class="timeline-text">${formattedText}</div>
            </div>
          `;
        } else {
          // No pipe separator, just regular text
          return `<p>${trimmedLine}</p>`;
        }
      }).join('');
      
      return processedLines;
    };
    
    const teamHTML = sortedMembers.map(member => `
      <div class="team-member" data-member-id="${member.id}">
        <div class="team-member-photo">
          <img src="${member.photo}" alt="${member.name}" />
        </div>
        <div class="team-member-info">
          <p class="team-member-name">${language === 'hu' ? member.name : (member.name_en || member.name)}</p>
          <p class="team-member-position">${language === 'hu' ? member.position : (member.position_en || member.position)}</p>
        </div>
        <div class="team-member-bio-container" data-bio-id="${member.id}">
          <div class="team-member-bio">${processBioText(language === 'hu' ? member.bio : (member.bio_en || member.bio))}</div>
        </div>
      </div>
    `).join('');
    
    return `
      <div class="team-section">
        <div class="team-grid">
          ${teamHTML}
        </div>
      </div>
    `;
  };

  // Replace [TEAM] marker with actual team HTML
  const processedContent = (htmlContent: string) => {
    // FIRST: Process asset markers with language support
    let contentWithAssets = processAssetMarkers(htmlContent, assets, language);
    
    // Process two-column layout with | separator
    // Use a temporary div to parse HTML properly
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = contentWithAssets;
    
    // Find all <p> tags that contain the pipe separator
    const paragraphs = tempDiv.querySelectorAll('p');
    paragraphs.forEach(p => {
      const text = p.innerHTML;
      
      // Check if paragraph contains pipe separator
      if (text.includes('|')) {
        // Split by pipe to get two columns
        const parts = text.split('|');
        
        if (parts.length >= 2) {
          const leftColumn = parts[0].trim();
          const rightColumn = parts.slice(1).join('|').trim(); // In case text contains multiple pipes
          
          // Replace ~ with <br> for line breaks within columns
          const formattedLeft = leftColumn.replace(/~/g, '<br>');
          const formattedRight = rightColumn.replace(/~/g, '<br>');
          
          // Create the two-column layout
          const columnDiv = document.createElement('div');
          columnDiv.className = 'two-column-layout';
          columnDiv.innerHTML = `
            <div class="column-left">${formattedLeft}</div>
            <div class="column-right">${formattedRight}</div>
          `;
          
          // Replace the paragraph with the column div
          p.replaceWith(columnDiv);
        }
      }
    });
    
    // NOW replace [TEAM] marker in the processed content
    let finalContent = tempDiv.innerHTML.replace('[TEAM]', generateTeamHTML());
    
    return finalContent;
  };

  return (
    <section id="about" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div>
          <div>
            <div className="text-gray-600">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-pulse text-gray-400"></div>
                </div>
              ) : aboutContent ? (
                <>
                  <div 
                    className="prose prose-gray max-w-none text-gray-700 leading-relaxed"
                    lang={language}
                    dangerouslySetInnerHTML={{ 
                      __html: processedContent(language === 'hu' ? aboutContent.hu : aboutContent.en) 
                    }} 
                  />
                  
                  
                </>
              ) : (
                <div>
                  <p>{t('about.text1')}</p>
                  <p>{t('about.text2')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}